import { useEffect, useState } from "react";
import { BackLink } from "../ui/BackLink";
import { Link } from "react-router-dom";
import { Avatar } from "../ui/Avatar";
import {
  OWNERS,
  assignDuplicateGroup,
  dismissDuplicateGroup,
  duplicateCandidates,
  linkDuplicateAsFamily,
  mergePeople,
  type DuplicateCandidates,
  type DuplicateRow,
  type SharedIdentifierVerdict,
} from "../actions";
import { primaryPhone, usePeopleSearch, type Person } from "../data";
import { useAuth } from "../auth";
import { NOUNS, count } from "../hebrew";
import { IcCheck, IcMerge, IcPeople, IcPlus, IcSearch } from "../ui/icons";

const VERDICT: Record<
  SharedIdentifierVerdict,
  { label: string; hint: string; tint: string }
> = {
  same_person: {
    label: "אותו שם ואותו טלפון",
    hint: "אותו מספר ואותו שם (מאוחד אוטומטית).",
    tint: "rec-duplicate",
  },
  unclear: {
    label: "שמות דומים לבדיקה",
    hint: "אותו מספר טלפון, ושמות שדומים בחלקם (למשל כרטיס זוגי מול יחיד או שם קצר מול מלא).",
    tint: "rec-child",
  },
  family: {
    label: "שמות שונים באותו טלפון (משפחה)",
    hint: "אותו מספר טלפון אבל שמות פרטיים שונים — בדרך כלל הורה וילד או בני זוג.",
    tint: "rec-family",
  },
};

const FAMILY_ROLES: { id: string; label: string }[] = [
  { id: "father", label: "אבא" },
  { id: "mother", label: "אמא" },
  { id: "child", label: "ילד/ה" },
  { id: "spouse", label: "בן/בת זוג" },
];

export function DuplicatesPage() {
  const { role } = useAuth();
  const isAdmin = role === "owner" || role === "admin";

  const [data, setData] = useState<DuplicateCandidates | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<SharedIdentifierVerdict | "shira" | "all">("unclear");
  const [note, setNote] = useState<string | null>(null);

  const [live, setLive] = useState<DuplicateCandidates["groups"]>([]);

  useEffect(() => {
    let cancelled = false;
    duplicateCandidates(200)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLive(d.groups);

        const initialUnclear = d.groups.filter((g) => g.verdict === "unclear" && g.assignedTo !== "shira").length;
        const initialSame = d.groups.filter((g) => g.verdict === "same_person" && g.assignedTo !== "shira").length;
        const initialFamily = d.groups.filter((g) => g.verdict === "family" && g.assignedTo !== "shira").length;
        const initialShira = d.groups.filter((g) => g.assignedTo === "shira").length;

        if (initialSame > 0) {
          setFilter("same_person");
        } else if (initialUnclear > 0) {
          setFilter("unclear");
        } else if (initialFamily > 0) {
          setFilter("family");
        } else if (initialShira > 0) {
          setFilter("shira");
        } else {
          setFilter("unclear");
        }

        const notices: string[] = [];
        if ((d.staffDeletedCount ?? 0) > 0) {
          notices.push(`מחקנו אוטומטית ${d.staffDeletedCount} כרטיסי בדיקה שלך (+972503999262)`);
        }
        if ((d.autoMergedCount ?? 0) > 0) {
          notices.push(`איחדנו אוטומטית ${d.autoMergedCount} כרטיסים כפולים עם אותו טלפון ואותו שם בדיוק`);
        }
        if (notices.length > 0) {
          setNote(notices.join(" · ") + ".");
        }
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : "לא הצלחנו לטעון. נסו לרענן את הדף."));
    return () => {
      cancelled = true;
    };
  }, []);

  function onMerged(dropIds: string[], msg: string) {
    const dropped = new Set(dropIds);
    setLive(
      live
        .map((g) => ({
          ...g,
          people: g.people.filter((p) => !dropped.has(p.id)),
          relatedPeople: (g.relatedPeople ?? []).filter((p) => !dropped.has(p.id)),
        }))
        .filter((g) => g.people.length > 1),
    );
    setNote(msg);
  }

  function onResolvedGroup(groupIdent: string, handledIds: string[], msg: string) {
    const handled = new Set(handledIds);
    setLive(
      live
        .filter((g) => groupKey(g) !== groupIdent)
        .map((g) => ({
          ...g,
          people: g.people.filter((p) => !handled.has(p.id)),
          relatedPeople: (g.relatedPeople ?? []).filter((p) => !handled.has(p.id)),
        }))
        .filter((g) => g.people.length > 1),
    );
    setNote(msg);
  }

  function onAssignedToShira(groupIdent: string, msg: string) {
    setLive(
      live.map((g) => (groupKey(g) === groupIdent ? { ...g, assignedTo: "shira" } : g)),
    );
    setNote(msg);
  }

  const unclearCount = live.filter((g) => g.verdict === "unclear" && g.assignedTo !== "shira").length;
  const familyCount = live.filter((g) => g.verdict === "family" && g.assignedTo !== "shira").length;
  const samePersonCount = live.filter((g) => g.verdict === "same_person" && g.assignedTo !== "shira").length;
  const shiraCount = live.filter((g) => g.assignedTo === "shira").length;
  const totalLeft = live.length;
  const toDecide = samePersonCount + unclearCount;

  const groups = live.filter((g) => {
    if (filter === "all") return true;
    if (filter === "shira") return g.assignedTo === "shira";
    return g.verdict === filter && g.assignedTo !== "shira";
  });

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>כפילויות ומשפחות לפי טלפון</h1>
            <p className="sub">
              {data === null
                ? "בודקים ומאחדים כפילויות זהות אוטומטית…"
                : toDecide > 0
                  ? `${count(toDecide, NOUNS.group)} של שמות דומים לבדיקה`
                  : "כל הכפילויות עם שם זהה אוחדו אוטומטית"}
            </p>
          </div>
        </div>

        {data && (
          <div className="pilltabs" role="tablist">
            {(
              [
                ["unclear", "שמות דומים", unclearCount],
                ["family", "משפחה (הורה וילד)", familyCount],
                ["shira", "לטיפול שירה", shiraCount],
                ...(samePersonCount > 0
                  ? ([["same_person", "אותו שם", samePersonCount]] as const)
                  : []),
                ["all", "הכול", totalLeft],
              ] as const
            ).map(([id, label, countNum]) => (
              <button
                key={id}
                role="tab"
                aria-selected={filter === id}
                className={filter === id ? "on" : undefined}
                onClick={() => setFilter(id as typeof filter)}
              >
                {label}
                <span className="tab-count">{countNum}</span>
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="body">
        <BackLink fallback="/settings" label="חזרה להגדרות" />

        {error && (
          <div className="notice error">
            לא הצלחנו לחשב את הכפילויות.
            <br />
            <span className="faint faint-xs mono">{error}</span>
          </div>
        )}

        {note && <div className="notice info">{note}</div>}

        {filter === "family" && groups.length > 0 && (
          <div className="notice info">
            אלה כרטיסים עם <b>אותו מספר טלפון אבל שמות פרטיים שונים</b> (בדרך כלל ילדים שנרשמו עם הטלפון של ההורה).
            אפשר בלחיצה אחת לסווג מי ההורה ומי הילד/ה, להוסיף את ההורה השני אם יש לו טלפון נפרד, ולשמור אותם כמשפחה אחת.
          </div>
        )}

        {filter === "shira" && groups.length > 0 && (
          <div className="notice info">
            אלה קבוצות שהעברת לבדיקה של <b>שירה</b>. נוצרה עבורן גם משימה פתוחה תחת השם של שירה במסך המשימות.
          </div>
        )}

        {data === null && !error ? (
          <>
            <div className="skeleton" style={{ height: 150, marginBottom: 12 }} />
            <div className="skeleton" style={{ height: 150 }} />
          </>
        ) : groups.length === 0 ? (
          <div className="empty">
            <div className="empty-ico tint-green">
              <IcCheck size={26} />
            </div>
            <h3>אין כפילויות לטיפול בלשונית הזו</h3>
            <p>
              {filter === "all"
                ? "אין כרטיסים עם אותו טלפון או מייל שמחכים להחלטה."
                : filter === "shira"
                  ? "אין כרגע כפילויות שמסומנות לטיפול שירה."
                  : "הכול נקי בלשונית הזו. אפשר לעבור ללשונית אחרת למעלה."}
            </p>
          </div>
        ) : (
          groups.map((g) => (
            <DuplicateGroup
              key={`${groupKey(g)}:${g.people.map((p) => p.id).join(",")}`}
              group={g}
              isAdmin={isAdmin}
              onMerged={onMerged}
              onResolvedGroup={onResolvedGroup}
              onAssignedToShira={onAssignedToShira}
            />
          ))
        )}

        {data && groups.length > 0 && (
          <p className="faint" style={{ padding: "10px 6px" }}>
            {`${groups.length} קבוצות בלשונית הזו, מתוך ${totalLeft} בסך הכול.`}
          </p>
        )}
      </div>
    </div>
  );
}

function groupKey(g: { kind: string; identifier: string }): string {
  return `${g.kind}:${g.identifier}`;
}

function guessRoleForPerson(p: DuplicateRow, isPrimaryCandidate = false): string {
  if (p.householdRole === "father" || p.householdRole === "mother" || p.householdRole === "spouse") {
    return p.householdRole;
  }
  if (p.isChild || p.householdRole === "child") {
    return "child";
  }
  if (isPrimaryCandidate) {
    return "father";
  }
  return "child";
}

function defaultRolesForGroup(people: DuplicateRow[]): Record<string, string> {
  const keepId = bestCandidate(people);
  const out: Record<string, string> = {};
  for (const p of people) {
    out[p.id] = guessRoleForPerson(p, p.id === keepId);
  }
  return out;
}

function personToDuplicateRow(p: Person): DuplicateRow {
  const phone = primaryPhone(p);
  return {
    id: p.id,
    name: p.displayName ?? "ללא שם",
    phones: phone ? [phone] : [],
    emails: (p.emails ?? []).map((e) => e.value ?? "").filter(Boolean),
    owners: p.owners ?? [],
    tagCount: (p.tagIds ?? []).length,
    householdName: p.householdName ?? null,
    householdId: p.householdId ?? null,
    householdRole: p.householdRole ?? null,
    isChild: p.isChild === true,
    city: p.address?.city ?? null,
    lastSeenDate: null,
    recordsMerged: 1,
    notDuplicateOf: p.notDuplicateOfPersonIds ?? [],
  };
}

function DuplicateGroup({
  group,
  isAdmin,
  onMerged,
  onResolvedGroup,
  onAssignedToShira,
}: {
  group: DuplicateCandidates["groups"][number];
  isAdmin: boolean;
  onMerged: (dropIds: string[], msg: string) => void;
  onResolvedGroup: (groupIdent: string, handledIds: string[], msg: string) => void;
  onAssignedToShira: (groupIdent: string, msg: string) => void;
}) {
  const [extraPeople, setExtraPeople] = useState<DuplicateRow[]>([]);
  const allPeople = [...group.people, ...extraPeople];

  const [keepId, setKeepId] = useState(() => bestCandidate(group.people));
  const [familyMode, setFamilyMode] = useState(group.verdict === "family");
  const [roles, setRoles] = useState<Record<string, string>>(() => defaultRolesForGroup(group.people));
  const [searchingExtra, setSearchingExtra] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchResults = usePeopleSearch(searchingExtra ? searchTerm : "");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const v = VERDICT[group.verdict];
  const dropIds = allPeople.filter((p) => p.id !== keepId).map((p) => p.id);
  const allIds = allPeople.map((p) => p.id);

  const remainingRelated = (group.relatedPeople ?? []).filter(
    (r) => !allPeople.some((p) => p.id === r.id),
  );

  function addExtraPerson(row: DuplicateRow) {
    if (allPeople.some((p) => p.id === row.id)) return;
    setExtraPeople((prev) => [...prev, row]);
    setRoles((prev) => ({
      ...prev,
      [row.id]: guessRoleForPerson(row, true),
    }));
  }

  function addAllRelated() {
    if (remainingRelated.length === 0) return;
    setExtraPeople((prev) => [...prev, ...remainingRelated]);
    setRoles((prev) => {
      const next = { ...prev };
      for (const r of remainingRelated) {
        next[r.id] = guessRoleForPerson(r, true);
      }
      return next;
    });
  }

  function removeExtraPerson(id: string) {
    setExtraPeople((prev) => prev.filter((p) => p.id !== id));
    if (keepId === id && group.people[0]) {
      setKeepId(group.people[0].id);
    }
  }

  const fatherCount = allPeople.filter((p) => (roles[p.id] ?? "child") === "father").length;
  const motherCount = allPeople.filter((p) => (roles[p.id] ?? "child") === "mother").length;
  const spouseCount = allPeople.filter((p) => (roles[p.id] ?? "child") === "spouse").length;
  const hasRoleDuplicates = fatherCount > 1 || motherCount > 1 || spouseCount > 1;

  async function commitMergeNow() {
    if (dropIds.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await mergePeople(keepId, dropIds);
      onMerged(
        dropIds,
        `${dropIds.length > 1 ? `איחדנו ${dropIds.length} כרטיסים` : `איחדנו את הכרטיסים`} אל „${r.keepName}”` +
          (r.activitiesMoved > 0 ? ` · העברנו ${r.activitiesMoved} פעילויות.` : "."),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לאחד. נסו שוב.");
      setBusy(false);
    }
  }

  async function saveAsFamily() {
    setBusy(true);
    setErr(null);
    try {
      const r = await linkDuplicateAsFamily(allIds, roles);
      const mergedCnt = r.mergedIds?.length ?? 0;
      onResolvedGroup(
        groupKey(group),
        allIds,
        `שמרנו את משפחת ${r.householdName ?? ""}` +
          (mergedCnt > 0 ? ` ואיחדנו אוטומטית ${mergedCnt} כרטיסים כפולים של ההורים.` : " עם התפקידים שהגדרת."),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור כמשפחה. נסו שוב.");
      setBusy(false);
    }
  }

  async function dismissGroup() {
    setBusy(true);
    setErr(null);
    try {
      await dismissDuplicateGroup(allIds);
      onResolvedGroup(groupKey(group), allIds, "סימנו שאלה אנשים שונים. הקבוצה הוסרה מהרשימה.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לבצע את הפעולה. נסו שוב.");
      setBusy(false);
    }
  }

  async function assignToShira() {
    setBusy(true);
    setErr(null);
    try {
      await assignDuplicateGroup(allIds, "shira");
      setBusy(false);
      onAssignedToShira(
        groupKey(group),
        "העברנו את הקבוצה ללשונית 'לטיפול שירה' ופתחנו עבורה משימת בדיקה.",
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו להעביר לשירה. נסו שוב.");
      setBusy(false);
    }
  }

  const extraIds = new Set(extraPeople.map((p) => p.id));

  return (
    <article className="card" style={{ marginBottom: 14 }}>
      <div className="card-body row" style={{ alignItems: "flex-start" }}>
        <span className="rail-ico tint-slate">
          <IcMerge />
        </span>
        <div className="grow">
          <p className="name-row">
            {count(allPeople.length, { one: "כרטיס", many: "כרטיסים" })}{" "}
            {extraPeople.length > 0
              ? "בקבוצה הזו (כולל בני משפחה שהוספת)"
              : `עם אותו ${group.kind === "phone" ? "טלפון" : "מייל"}`}
          </p>
          <p className="muted mono" dir="ltr" style={{ textAlign: "right" }}>
            {group.identifier}
          </p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <span className={`rec-badge ${v.tint}`}>{v.label}</span>
            {group.assignedTo === "shira" && (
              <span className="rec-badge rec-family">לטיפול שירה</span>
            )}
          </div>
          <p className="faint" style={{ marginTop: 4 }}>
            {v.hint}
          </p>
        </div>
      </div>

      <div className="ask" style={{ paddingBottom: 8 }}>
        <div className="chips" style={{ marginBottom: 10 }}>
          <button
            type="button"
            className={!familyMode ? "tagchip tagchip-on" : "tagchip"}
            onClick={() => setFamilyMode(false)}
            disabled={busy}
          >
            זה אותו אדם (איחוד לכרטיס אחד)
          </button>
          <button
            type="button"
            className={familyMode ? "tagchip tagchip-on" : "tagchip"}
            onClick={() => setFamilyMode(true)}
            disabled={busy}
          >
            בני משפחה (סיווג הורה / ילד/ה)
          </button>
        </div>

        {!familyMode ? (
          <>
            <p className="ask-q">בחרו איזה כרטיס להשאיר (השאר יתמזגו אליו):</p>
            {allPeople.map((p) => (
              <label
                key={p.id}
                className="dup-row"
                style={{
                  background: keepId === p.id ? "var(--orange-soft)" : undefined,
                  borderColor: keepId === p.id ? "var(--orange)" : "var(--line)",
                }}
              >
                <input
                  type="radio"
                  name={`keep-${group.identifier}`}
                  checked={keepId === p.id}
                  onChange={() => setKeepId(p.id)}
                  disabled={busy}
                />
                <Avatar name={p.name || "?"} size={36} />
                <span className="grow">
                  <span className="name-row">{p.name || "ללא שם"}</span>
                  <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                    {p.phones.join(" · ") || "—"}
                  </span>
                  {p.emails.length > 0 && (
                    <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                      {p.emails.join(" · ")}
                    </span>
                  )}
                  <span className="faint" style={{ display: "block", marginTop: 2 }}>
                    {[
                      p.householdRole ? roleLabel(p.householdRole) : null,
                      p.householdName ? `משפחת ${p.householdName}` : null,
                      p.city,
                      p.tagCount > 0 ? `${p.tagCount} תגיות` : null,
                      p.owners.length > 0
                        ? p.owners.map((o) => OWNERS.find((x) => x.slug === o)?.label ?? o).join("+")
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  {extraIds.has(p.id) && (
                    <button
                      type="button"
                      className="tag-act"
                      onClick={(e) => {
                        e.preventDefault();
                        removeExtraPerson(p.id);
                      }}
                      disabled={busy}
                    >
                      הסר
                    </button>
                  )}
                  <Link to={`/people/${p.id}`} className="tag-act">
                    פתיחה
                  </Link>
                </div>
              </label>
            ))}
          </>
        ) : (
          <>
            <p className="ask-q">
              סמנו את התפקיד של כל אחד במשפחה (אם יש כמה כרטיסים לאבא או לאמא, סמנו את כולם והמערכת תאחד אותם אוטומטית):
            </p>
            {allPeople.map((p) => {
              const currentRole = roles[p.id] ?? "child";
              return (
                <div key={p.id} className="dup-row" style={{ flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                    <Avatar name={p.name || "?"} size={36} />
                    <span className="grow">
                      <span className="name-row">{p.name || "ללא שם"}</span>
                      <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                        {p.phones.join(" · ") || "—"}
                      </span>
                      <span className="faint" style={{ display: "block" }}>
                        {[
                          p.householdName ? `משפחת ${p.householdName}` : null,
                          p.city,
                          p.tagCount > 0 ? `${p.tagCount} תגיות` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      {extraIds.has(p.id) && (
                        <button
                          type="button"
                          className="tag-act"
                          onClick={() => removeExtraPerson(p.id)}
                          disabled={busy}
                        >
                          הסר
                        </button>
                      )}
                      <Link to={`/people/${p.id}`} className="tag-act">
                        פתיחה
                      </Link>
                    </div>
                  </div>

                  <div className="chips" style={{ marginBottom: 0 }}>
                    {FAMILY_ROLES.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        className={currentRole === r.id ? "tagchip tagchip-on" : "tagchip"}
                        onClick={() => setRoles((prev) => ({ ...prev, [p.id]: r.id }))}
                        disabled={busy}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            {hasRoleDuplicates && (
              <div className="notice info" style={{ marginTop: 8, marginBottom: 4 }}>
                סימנת{" "}
                {[
                  fatherCount > 1 ? `${fatherCount} כרטיסים בתור „אבא”` : null,
                  motherCount > 1 ? `${motherCount} כרטיסים בתור „אמא”` : null,
                  spouseCount > 1 ? `${spouseCount} כרטיסים בתור „בן/בת זוג”` : null,
                ]
                  .filter(Boolean)
                  .join(" ו־")}
                . בלחיצה על השמירה, המערכת <b>תאחד אוטומטית</b> את הכרטיסים הכפולים של אותו הורה לכרטיס אחד ותקשר את כולם למשפחה אחת.
              </div>
            )}
          </>
        )}

        {remainingRelated.length > 0 && (
          <div
            style={{
              marginTop: 10,
              padding: 12,
              background: "var(--bg-soft)",
              border: "1px solid var(--line)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <span className="grow name-row">
                מצאנו עוד {remainingRelated.length === 1 ? "כרטיס אחד" : `${remainingRelated.length} כרטיסים`} עם אותו שם משפחה בטלפון אחר:
              </span>
              {remainingRelated.length > 1 && (
                <button
                  type="button"
                  className="btn"
                  onClick={addAllRelated}
                  disabled={busy}
                  style={{ padding: "4px 10px" }}
                >
                  <IcPlus size={14} /> הוסף את כל ה־{remainingRelated.length} לקבוצה
                </button>
              )}
            </div>
            {remainingRelated.map((rp) => (
              <div
                key={rp.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 0",
                  borderTop: "1px solid var(--line)",
                }}
              >
                <Avatar name={rp.name || "?"} size={30} />
                <span className="grow">
                  <span className="name-row">{rp.name}</span>
                  <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                    {rp.phones.join(" · ") || "אין טלפון"}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn"
                  onClick={() => addExtraPerson(rp)}
                  disabled={busy}
                  style={{ padding: "4px 10px" }}
                >
                  <IcPlus size={14} /> הוסף לקבוצה
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          {!searchingExtra ? (
            <button
              type="button"
              className="btn"
              onClick={() => setSearchingExtra(true)}
              disabled={busy}
            >
              <IcPlus size={15} /> הוסף בן משפחה / כרטיס נוסף מחיפוש
            </button>
          ) : (
            <div
              style={{
                padding: 12,
                background: "var(--bg-soft)",
                border: "1px solid var(--line)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div className="searchbar grow" style={{ margin: 0 }}>
                  <IcSearch size={16} />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="חפשו לפי שם או טלפון להוספה לקבוצה…"
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setSearchingExtra(false);
                    setSearchTerm("");
                  }}
                >
                  סגור
                </button>
              </div>
              {searchTerm.trim().length > 0 && (
                <div>
                  {searchResults.data
                    .filter((p) => !allPeople.some((ap) => ap.id === p.id))
                    .slice(0, 6)
                    .map((p) => (
                      <div
                        key={p.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "6px 0",
                          borderTop: "1px solid var(--line)",
                        }}
                      >
                        <Avatar name={p.displayName ?? "?"} size={30} />
                        <span className="grow">
                          <span className="name-row">{p.displayName ?? "ללא שם"}</span>
                          <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                            {primaryPhone(p) ?? "אין טלפון"}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => addExtraPerson(personToDuplicateRow(p))}
                        >
                          <IcPlus size={14} /> הוסף
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="ask">
        {err && (
          <div className="notice error" style={{ marginTop: 0, marginBottom: 10 }}>
            {err}
          </div>
        )}
        <div className="btn-row">
          {!familyMode ? (
            isAdmin && (
              <button className="btn btn-primary" onClick={commitMergeNow} disabled={busy}>
                {busy ? "מאחדים…" : `אחד ל„${allPeople.find((p) => p.id === keepId)?.name ?? "כרטיס נבחר"}”`}
              </button>
            )
          ) : (
            <button className="btn btn-primary" onClick={saveAsFamily} disabled={busy}>
              {busy
                ? "שומרים משפחה…"
                : hasRoleDuplicates
                  ? "שמור כמשפחה ואחד כפילויות"
                  : "שמור כמשפחה"}
            </button>
          )}
          {group.assignedTo !== "shira" && (
            <button className="btn" onClick={assignToShira} disabled={busy}>
              העבר לטיפול שירה
            </button>
          )}
          <button className="btn" onClick={dismissGroup} disabled={busy}>
            אנשים שונים (התעלם)
          </button>
        </div>
      </div>
    </article>
  );
}

function bestCandidate(people: DuplicateRow[]): string {
  const score = (p: DuplicateRow) =>
    p.phones.length * 3 +
    p.emails.length * 3 +
    p.tagCount +
    p.recordsMerged * 2 +
    (p.householdName ? 2 : 0) +
    (p.city ? 1 : 0) +
    (p.isChild ? -5 : 0);
  return [...people].sort((a, b) => score(b) - score(a) || a.id.localeCompare(b.id))[0]!.id;
}

function roleLabel(r: string): string {
  const map: Record<string, string> = {
    father: "אבא",
    mother: "אמא",
    child: "ילד/ה",
    spouse: "בן/בת זוג",
    בוגר: "בוגר",
  };
  return map[r] ?? r;
}

export const DUPLICATES_ICON = IcPeople;
